import React, { useEffect, useState } from "react";
import { HeroTop } from "../hero-top";
import { ReferSection } from "../refer-section";
import { PrizesSection } from "../prizes-section";
import { Qa } from "../qa";
import Space from "components/common/space";
import T from "@marginly/ui/components/typography";
import * as S from "./styled";
import { useMemberInfo } from "components/registration/hooks/use-member-info";
import { RegisterForm } from "../register-form";
import { HeroLayout } from "../styled/hero-layout";
import { useAccount } from "wagmi";
import { useReferralsList } from "components/leaderboard/hooks/use-referrals-list";
import { RefferalRankingModal } from "components/referral-ranking-modal";
import { LeaderboardSection } from "components/leaderboard-section";
import { IndividualRatingModal } from "components/individual-rating-modal";
import { useMemberPlacement } from "components/leaderboard/hooks/use-member-placement";
import { EmailButton } from "components/leaderboard/styled";
import {
  getCamelotPrizeNumber,
  getCamelotRefPrizeNumber,
} from "components/leaderboard/utils/get-prize-number";
import {
  BADGES_CAMELOT,
  REFER_BADGES_CAMELOT,
} from "components/leaderboard/hooks/use-rating";
import { RankInfo } from "../rank-info";
import { CamelotIcon } from "../camelot-icon";
import { useLeaderboardList } from "components/leaderboard/hooks/use-leaderboard-list";
import { formatNum } from "util/format";
import { CAMELOT_LINK } from "constants/common";

const BLOG_URL =
  "https://blog.marginly.com/camelot-trading-competition-the-fun-continues/";

const goTradeLink = (
  <S.SignInRow>
    <EmailButton as="a" href={CAMELOT_LINK} className="white">
      <T action>Go Trade</T>
    </EmailButton>
  </S.SignInRow>
);

const getRefRankImg = (placeNumber: number | null | undefined) => {
  return `/contest/images/referral/thumbnail-${getCamelotRefPrizeNumber(
    placeNumber,
  )}.png`;
};

const getRankImg = (placeNumber: number | null | undefined) => {
  return `/contest/images/prizes/${getCamelotPrizeNumber(placeNumber)}.png`;
};

const TOP_RANK = 50;

interface Props {
  contestId: number;
}

export function CamelotLive({ contestId }: Props) {
  const { memberInfo, refetch } = useMemberInfo(contestId);
  const [isIndividualVisible, setIsIndividualVisible] = useState(false);
  const [isReferralRankingVisible, setIsReferralRankingVisible] =
    useState(false);
  const { address } = useAccount();
  const [userAddress, setUserAddress] = useState<string | undefined>(undefined);
  useEffect(() => {
    setUserAddress(address);
  }, [address]);

  const referralsList = useReferralsList(contestId);
  const leaderboardList = useLeaderboardList(contestId);
  const refCode = memberInfo?.refCode;
  const memberPlacement = useMemberPlacement(contestId);
  const rank = memberPlacement?.place === -1 ? null : memberPlacement?.place;

  const userReferralIndex = referralsList?.top?.findIndex(
    (item) => item.userAddress === userAddress,
  );

  const userReferralPlace = userReferralIndex === -1 ? null : userReferralIndex;

  const renderInfo = (prizeSum: number) => {
    return (
      <>
        <T headerL>Your rank #{formatNum(rank || undefined)}</T>

        <Space height={12} heightMobile={4} />

        {prizeSum ? (
          <T secondary body>
            Current possible reward is ${formatNum(prizeSum)}.
          </T>
        ) : (
          <T secondary body>
            Compete in the trading contest until
            February&nbsp;1&nbsp;and&nbsp;get&nbsp;rewards.
          </T>
        )}
      </>
    );
  };

  const renderTop = () => {
    if (!userAddress || !refCode) {
      return (
        <>
          <HeroTop />
          <Space height={50} heightMobile={48} />
          <T body>
            Compete in the trading contest until March 27th and get rewards.
          </T>
          <Space height={50} heightMobile={48} />
          <RegisterForm
            refCode={refCode}
            contestId={contestId}
            onSubmit={refetch}
          />
        </>
      );
    }
    if (rank === null) {
      return (
        <>
          <T headerL>You are registered</T>
          <Space height={12} heightMobile={12} />
          <T body secondary>
            Compete in the trading contest until March 27th and get rewards.
          </T>
          <Space height={48} heightMobile={48} />
          {goTradeLink}
        </>
      );
    }

    return (
      <RankInfo
        badges={BADGES_CAMELOT}
        getPrizeNumber={getCamelotPrizeNumber}
        getRankImg={getRankImg}
        getRefRankImg={getRefRankImg}
        userReferralPlace={userReferralPlace}
        getReferralPrizeNumber={getCamelotRefPrizeNumber}
        referBadges={REFER_BADGES_CAMELOT}
        topRank={TOP_RANK}
        memberPlacement={memberPlacement}
        rank={rank}
        tradeLink={goTradeLink}
        renderInfo={renderInfo}
      />
    );
  };

  return (
    <div>
      <HeroLayout>{renderTop()}</HeroLayout>
      <Space height={162} heightMobile={96} />
      <ReferSection
        refCode={refCode}
        onShowMoreButtonClick={() => setIsReferralRankingVisible(true)}
      />
      <Space height={172} heightMobile={72} />
      <PrizesSection />
      <Space height={64} heightMobile={32} />
      <S.LeverageBanner href={BLOG_URL} target="_blank" rel="noreferrer">
        <div>
          <S.BannerIconWrapper>
            <CamelotIcon />
          </S.BannerIconWrapper>
          <T body>Only trades with $100+ margin count.</T>
        </div>
        <img src="/contest/images/arr-right.svg" alt="" />
      </S.LeverageBanner>
      <Space height={172} heightMobile={96} />
      <LeaderboardSection
        contestDates="Mar 13 to Mar 27"
        renderImgReferPath={(placeNumber: number) =>
          `/contest/images/referral/thumbnail-${placeNumber}.png`
        }
        isContestOver={false}
        list={leaderboardList}
        onReferralButtonClick={() => setIsReferralRankingVisible(true)}
        onTopRatingButtonClick={() => setIsIndividualVisible(true)}
        userAddress={userAddress}
        renderImgPath={getRankImg}
        topRank={TOP_RANK}
      />
      <Space height={172} heightMobile={96} />
      <Qa />
      {isReferralRankingVisible && (
        <RefferalRankingModal
          renderImgPath={getRefRankImg}
          onHide={() => setIsReferralRankingVisible(false)}
          address={userAddress}
          list={referralsList}
        />
      )}
      {isIndividualVisible && (
        <IndividualRatingModal
          topRank={TOP_RANK}
          renderImgPath={getRankImg}
          onHide={() => setIsIndividualVisible(false)}
          userAddress={userAddress}
          list={leaderboardList}
        />
      )}
    </div>
  );
}
